import type { Page, ACF } from 'types';
import React, { memo } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useShareImages } from 'hooks/useShareImages';

const WebPageJson: React.FC<
  Pick<Page, 'date' | 'modified' | 'seo' | 'title'> &
    Pick<ACF, 'name' | 'description' | 'imageObjectCaption'> & {
      siteUrl: string;
      siteDescription: string;
      stateName: string;
    }
> = memo(
  ({
    seo,
    date,
    name,
    title,
    modified,
    siteUrl,
    stateName,
    description,
    siteDescription,
    imageObjectCaption,
  }) => {
    const { locale } = useRouter();
    const webpageImage = useShareImages({
      type: 'full',
      title,
    });

    let language = 'en-US';
    let url = siteUrl;

    if (locale === 'es') {
      language = 'es-ES';
      url = `${url}/es`;
    }

    const details = {
      '@context': 'https://schema.org/',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': seo?.canonical,
          url: seo?.canonical,
          name: name || title,
          primaryImageOfPage: {
            '@id': `${seo?.canonical}/#primaryimage`,
          },
          image: {
            '@id': `${seo?.canonical}/#primaryimage`,
          },
          thumbnailUrl: webpageImage.url,
          datePublished: date,
          dateModified: modified,
          description: description || seo?.description,
          breadcrumb: {
            '@id': `${seo?.canonical}/#breadcrumb`,
          },
          inLanguage: language,
          potentialAction: [
            {
              '@type': 'ReadAction',
              target: [seo?.canonical],
            },
          ],
        },
        {
          '@type': 'ImageObject',
          inLanguage: language,
          '@id': `${seo?.canonical}/#primaryimage`,
          url: webpageImage.url,
          contentUrl: webpageImage.url,
          width: webpageImage.width,
          height: webpageImage.height,
          caption: imageObjectCaption || title,
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${seo?.canonical}/#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: url,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: stateName,
            },
          ],
        },
        {
          '@type': 'WebSite',
          '@id': `${url}/#website`,
          url: url,
          name: 'Leafwell',
          description: siteDescription,
          publisher: {
            '@id': 'https://leafwell.com/#organization',
          },
          potentialAction: [
            {
              '@type': 'SearchAction',
              target: {
                '@type': 'EntryPoint',
                urlTemplate: 'https://leafwell.com/?s={search_term_string}',
              },
              'query-input': 'required name=search_term_string',
            },
          ],
          inLanguage: language,
        },
        {
          '@type': 'Organization',
          '@id': 'https://leafwell.com/#organization',
          name: 'Leafwell',
          url: 'https://leafwell.com',
          logo: {
            '@type': 'ImageObject',
            inLanguage: language,
            '@id': 'https://www.leafwell.com/#/schema/logo/image/',
            url: 'https://admin.leafwell.com/app/uploads/2021/10/leafwell-icon.jpg',
            contentUrl:
              'https://admin.leafwell.com/app/uploads/2021/10/leafwell-icon.jpg',
            width: 512,
            height: 512,
            caption: 'Leafwell',
          },
          sameAs: [
            'https://www.facebook.com/leafwellofficial/',
            'https://twitter.com/_Leafwell_',
            'https://www.instagram.com/leafwell_official/',
            'https://www.linkedin.com/company/leafwellofficial/',
            'https://www.pinterest.com/leafwell_official/',
            'https://www.youtube.com/channel/UCok2ps-pUDEA885yqTaZLgA/',
          ],
        },
      ],
    };

    return React.createElement(
      Head,
      null,
      React.createElement('script', {
        type: 'application/ld+json',
        dangerouslySetInnerHTML: {
          __html: JSON.stringify(details),
        },
      }),
    );
  },
);

export default WebPageJson;
